import { useContext, useMemo, useState } from "react";
import { Context } from "../../../../context/Context";
import { ActionMenu, Button, Checkbox, Expand, Eye, EyeOff, GripVertical, Shrink } from "../../../ui";
import { calculateEffectivePrice, formatDiscount, getDefaultPricing, pricingLabel } from "../../../../utils/menuPricing";
import { integrationLabel } from "../../../../utils/integrations";
import { sendToAppText } from "../../sendToApp/sendToAppText";
import SendBlockReason from "../../sendToApp/SendBlockReason";
import "../../sendToApp/SendToApp.css";

const sentDate = (value, lang) => new Date(value).toLocaleString(lang === "ar" ? "ar-SA" : "en-GB", { dateStyle: "medium", timeStyle: "short" });

// One chip per app that was ever sent this item, from the backend's status:
// "HungerStation ✓" = the current version was sent, "Jahez ↻" = only an older
// version was sent (the item changed afterwards). Never-sent apps show nothing.
// History is immutable; clicking opens the item's send events.
function SentStatus({ sends, lang, onOpen }) {
  const t = sendToAppText(lang);
  const entries = (sends || []).filter((entry) => entry.status !== "never_sent" || entry.deleteRequestedAt);
  if (!entries.length) return null;
  const stale = entries.some((entry) => entry.status === "updated_since_sent");
  const detail = (entry) => [entry.status !== "never_sent" && t.statusDetail(integrationLabel(entry.app, lang), entry.status === "updated_since_sent" ? t.sentUpdated : t.sentCurrent, sentDate(entry.lastSentAt, lang), entry.sendCount), entry.deleteRequestedAt && `${t.deleteRequested(integrationLabel(entry.app, lang))} (${sentDate(entry.deleteRequestedAt, lang)})`].filter(Boolean).join(" · ");
  return (
    <button type="button" className="food-sent-row" title={entries.map(detail).join("\n")} aria-label={entries.map(detail).join("; ")} onClick={onOpen}>
      {entries.map((entry) => (
        <span key={entry.app} className="food-sent-group">
          {entry.status !== "never_sent" && (
            <span className={`food-sent-chip ${entry.status === "updated_since_sent" ? "stale" : ""}`}>
              {integrationLabel(entry.app, lang)} {entry.status === "updated_since_sent" ? "↻" : "✓"}
            </span>
          )}
          {entry.deleteRequestedAt && <span className="food-sent-chip delete">{t.deleteRequested(integrationLabel(entry.app, lang))}</span>}
        </span>
      ))}
      {(stale || entries.some((entry) => entry.lastSentAt)) && <span className="food-sent-date">{stale ? t.updatedLegend : sentDate(entries.find((entry) => entry.lastSentAt).lastSentAt, lang)}</span>}
    </button>
  );
}

const LABELS = {
  ar: { edit: "تعديل", archive: "أرشفة", copy: "نسخ", history: "السجل", expand: "تكبير", shrink: "تصغير", image: "الصورة", hide: "إخفاء في كل الفروع", show: "إظهار في كل الفروع", hideBranch: "إخفاء في هذا الفرع", showBranch: "إظهار في هذا الفرع", hidden: "مخفي", hiddenIn: (n) => `مخفي في ${n} فرع`, hiddenHere: "مخفي في هذا الفرع", noImage: "لا توجد صورة", noDescription: "لا يوجد وصف عربي", mandatory: "إلزامي", calories: "سعرة" },
  en: { edit: "Edit", archive: "Archive", copy: "Copy", history: "History", expand: "Expand", shrink: "Shrink", image: "Image", hide: "Hide in all branches", show: "Show in all branches", hideBranch: "Hide in this branch", showBranch: "Show in this branch", hidden: "Hidden", hiddenIn: (n) => `Hidden in ${n} branches`, hiddenHere: "Hidden in this branch", noImage: "No image", noDescription: "No Arabic description", mandatory: "Mandatory", calories: "kcal" },
};

const currency = (value, lang) => (value === null || value === undefined ? "—" : `${value} ${lang === "ar" ? "ر" : "SAR"}`);

function PriceBlock({ pricing = [], lang }) {
  const defaultEntry = getDefaultPricing(pricing);
  const [selectedName, setSelectedName] = useState(defaultEntry?.name);
  const selected = pricing.find((entry) => entry.name === selectedName) || defaultEntry;
  if (!selected) return <strong className="final-price">—</strong>;
  const effective = selected.effectivePrice ?? calculateEffectivePrice(selected.price, selected.discount);
  return (
    <>
      <div className="food-price-block">
        <strong className="final-price">{currency(effective, lang)}</strong>
        {selected.discount && effective !== selected.price && <span className="old-price">{currency(selected.price, lang)}</span>}
        {selected.discount && <span className="food-discount-tag">−{formatDiscount(selected.discount, lang)}</span>}
      </div>
      {pricing.length > 1 && (
        <div className="food-addcost-box" role="group" aria-label={lang === "ar" ? "الأسعار" : "Prices"}>
          {pricing.map((entry) => (
            <button type="button" key={entry.name} title={pricingLabel(entry.name, lang)} aria-pressed={selected.name === entry.name} className={`add-cost-circle ${selected.name === entry.name ? "selected" : ""}`} onClick={() => setSelectedName(entry.name)}>
              <span>{pricingLabel(entry.name, lang)}</span> {entry.effectivePrice ?? calculateEffectivePrice(entry.price, entry.discount)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

function Additions({ groups = [], lang, cardKey, selectedOptions, setSelectedOptions }) {
  if (!groups.length) return null;
  return (
    <div className="food-card-additions">
      {groups.map((group, addIndex) => {
        const addition = group.addition || {};
        const key = `${cardKey}-${addIndex}`;
        return (
          <div className="food-add-group" key={group._id || addIndex}>
            <div className="food-add-title">
              <div>
                <span>
                  {group.order || addIndex + 1}. {addition.title?.ar || addition.title?.en || (addition.missing ? "?" : "")}
                </span>
                {addition.title?.en && <small>{addition.title.en}</small>}
              </div>
              <div className="food-add-badges">
                {addition.isMandatory && <b>{LABELS[lang]?.mandatory}</b>}
                <span className="food-minmax">
                  min {addition.min ?? 0} - max {addition.max ?? 0}
                </span>
              </div>
            </div>
            <div className="food-add-options">
              {(addition.options || []).map((option, optionIndex) => {
                const selected = selectedOptions?.[key] === optionIndex;
                return (
                  <button key={optionIndex} type="button" aria-pressed={selected} className={`food-option ${selected ? "selected" : ""}`} onClick={() => setSelectedOptions?.((prev) => ({ ...prev, [key]: prev[key] === optionIndex ? undefined : optionIndex }))}>
                    <span>{option.name?.ar || option.name?.en}</span>
                    {option.name?.en && <small>{option.name.en}</small>}
                    {Number(option.price) !== 0 && <strong>{option.price > 0 ? "+" : ""}{option.price} {lang === "ar" ? "ر" : "SAR"}</strong>}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function FoodItemCard({
  item,
  menu,
  role,
  selectedBranchId,
  cardKey,
  expanded,
  onToggleExpand,
  selectedOptions,
  setSelectedOptions,
  onToggleBranch,
  actions = [],
  actionsLabel,
  draggable = false,
  isDragging = false,
  onDragStart,
  onDragEnter,
  onDragEnd,
  selectMode = false,
  selected = false,
  onToggleSelect,
  sentTo,
  onOpenSendHistory,
  sendBlock = null,
}) {
  const { lang } = useContext(Context);
  const t = LABELS[lang] || LABELS.ar;
  const hiddenBranches = item.visibility?.hiddenBranches || [];
  const hiddenEverywhere = Boolean(item.visibility?.hidden);
  const hiddenInSelected = Boolean(selectedBranchId && hiddenBranches.some((entry) => String(entry.branch) === String(selectedBranchId)));
  const additionsKey = useMemo(() => `${menu._id}-${item._id}`, [menu._id, item._id]);

  return (
    <article
      className={`food-card ${selectMode && !sendBlock ? "is-selectable" : ""} ${selectMode && sendBlock ? "is-unavailable" : ""} ${selected ? "is-selected" : ""} ${hiddenEverywhere || hiddenInSelected ? "hidden-card" : ""} ${expanded ? "expanded-card" : ""} ${isDragging ? "is-dragging" : ""}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnter={onDragEnter}
      onDragOver={(event) => draggable && event.preventDefault()}
      onDragEnd={onDragEnd}
      onClick={selectMode && !sendBlock ? (event) => !event.target.closest("button, a, input, label, [role='menu']") && onToggleSelect?.(item) : undefined}
    >
      {selectMode && sendBlock && <SendBlockReason block={sendBlock} />}
      {selectMode && (
        <div className="food-select-box">
          <Checkbox checked={selected} disabled={Boolean(sendBlock)} onChange={() => onToggleSelect?.(item)} aria-label={sendToAppText(lang).selectItem(item.name?.ar || item.name?.en || "")} />
        </div>
      )}
      <div className="food-badges">
        {hiddenEverywhere && <span className="food-badge">{t.hidden}</span>}
        {hiddenInSelected && <span className="food-badge">{t.hiddenHere}</span>}
        {!selectedBranchId && hiddenBranches.length > 0 && <span className="food-badge">{t.hiddenIn(hiddenBranches.length)}</span>}
      </div>

      <div className="food-image-placeholder">
        {item.image ? <img src={item.image} alt={item.name?.ar || item.name?.en} /> : <span>{t.noImage}</span>}
      </div>

      <div className="food-card-body">
        <div className="food-card-top">
          <div className="food-card-title">
            <h3>
              {item.order}. {item.name?.ar || item.name?.en}
            </h3>
            {item.name?.en && <p>{item.name.en}</p>}
          </div>
          <PriceBlock pricing={item.pricing} lang={lang} />
        </div>

        <SentStatus sends={sentTo} lang={lang} onOpen={() => onOpenSendHistory?.(item)} />

        <div className="food-desc-box">
          <p className="food-desc-ar">{item.description?.ar || t.noDescription}</p>
          {item.description?.en && <p className="food-desc-en">{item.description.en}</p>}
        </div>

        <div className="food-meta">
          {(item.calories?.ar || item.calories?.en) && (
            <span className="tag calories">
              🔥 {item.calories?.ar || item.calories?.en} {t.calories}
            </span>
          )}
          {(item.allergens?.ar || item.allergens?.en) && <span className="tag allergen">⚠️ {item.allergens?.ar || item.allergens?.en}</span>}
        </div>

        <Additions groups={item.additionGroups} lang={lang} cardKey={additionsKey} selectedOptions={selectedOptions} setSelectedOptions={setSelectedOptions} />

        <div className="food-card-footer">
          {draggable && <GripVertical size={16} className="food-drag-handle" aria-hidden="true" />}
          {selectedBranchId && onToggleBranch && (
            <Button type="button" variant="warning" size="sm" rounded className="food-action-btn" title={hiddenInSelected ? t.showBranch : t.hideBranch} aria-label={hiddenInSelected ? t.showBranch : t.hideBranch} icon={hiddenInSelected ? <Eye size={16} /> : <EyeOff size={16} />} onClick={() => onToggleBranch(item, selectedBranchId, hiddenInSelected)} />
          )}
          <Button type="button" variant="primary" size="sm" rounded className="food-action-btn" title={expanded ? t.shrink : t.expand} aria-label={expanded ? t.shrink : t.expand} icon={expanded ? <Shrink size={16} /> : <Expand size={16} />} onClick={onToggleExpand} />
          <ActionMenu label={actionsLabel} items={actions} buttonClassName="food-action-btn" />
        </div>
      </div>
    </article>
  );
}
